import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { LogbookLeg } from '@shared/models/activity-summary/logbook-leg';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import {
  Map,
  LatLngExpression,
} from 'leaflet'
import { designateTagType } from '@shared/utilities/utilities';
import { ButtonModule } from 'primeng/button';
import { FieldsetModule } from 'primeng/fieldset';
import { FormatTimePipe, FormatMinutes } from '@shared/pipes/format-time.pipe';
import { FlightdetailService } from './service/flightdetail.service';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { BehaviorSubject, combineLatest, map, Observable, Subscription, switchMap, tap } from 'rxjs';
import { FlightCrew } from '@shared/models/activity-summary/flight-crew';
import { SignalsService } from '@shared/services/signals/signals.service';
import { CrewMember } from 'app/features/flights/flight-detail/model/crew-member';
import { CrewDetail } from './model/crew-detail';
import { Sequences } from '@shared/models/activity-summary/sequences';
import { MapService } from '../services/map.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CrewContactInfoComponent } from './crew-contact-info/crew-contact-info.component';
import { SearchResult } from 'app/features/search/model/search-result';
import { DateTime } from 'luxon';
import { AppService } from 'app/app.service';
import { AppSettings } from '@shared/models/settings/app-settings';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { FlightDetailDialogComponent } from './flight-detail-dialog/flight-detail-dialog.component';
import { Aircraft } from '@shared/models/activity-summary/aircraft';
import { RegDetailsDialogComponent } from './reg-details-dialog/reg-details-dialog.component';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { AIRLINE_CODES } from '@shared/utilities/airline-codes';

@Component({
  selector: 'app-flight-detail',
  standalone: true,
  imports: [
    CommonModule,
    LeafletModule,
    ButtonModule,
    FieldsetModule,
    FormatTimePipe,
    FormatMinutes,
    DatePipe,
    BreadcrumbComponent,
    OverlayPanelModule
  ],
  templateUrl: './flight-detail.component.html',
  styleUrl: './flight-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlightDetailComponent implements OnInit, AfterViewChecked, OnDestroy {

  ref: DynamicDialogRef | undefined;

  private map?: Map;
  private geoLineColor?: string;

  airlineCodes = AIRLINE_CODES;
  sequence?: Sequences;
  leg?: LogbookLeg;
  isTimeFormatUTC?: boolean;

  //Used when navigation from search results
  searchResponse?: SearchResult;

  tag: string = '---';
  crewMembers$?: Observable<RequestState<CrewDetail[]>>;

  /** Employee number making the request for
   *  an employee contact details
   * */
  intiatorEmpNum$ = this.signalsService.employeeNumber$;

  /** Employee number being requestedto get 
  * contact details for 
  */
  _requestedEmpNum$ = new BehaviorSubject<string>('')
  requestedEmpNum$ = this._requestedEmpNum$.asObservable();

  theme$?: string;
  sub?: Subscription;


  takeOffIconLight = '/assets/icons/aircraft/takeoffLight.svg';
  takeOffIconDark = '/assets/icons/aircraft/takeoffDark.svg';
  landingIconLight = '/assets/icons/aircraft/landingLight.svg';
  landingIconDark = '/assets/icons/aircraft/landingDark.svg';
  contactLight = '/assets/icons/contact/contactIconLight_ver1.svg';
  contactDark = '/assets/icons/contact/contactIconDark_ver1.svg';

  constructor(
    public appService: AppService,
    private flightDetailService: FlightdetailService,
    private signalsService: SignalsService,
    private mapService: MapService,
    private dialogService: DialogService,
    private errorLogService: ErrorLogService,
    private cdr: ChangeDetectorRef,
  ) { 
    
  }

  ngOnInit(): void {
    this.sub = this.appService.theme$.subscribe( (theme) =>{
        this.theme$ = theme;
        this.cdr.markForCheck();
    });

    const settings = localStorage.getItem('appSettings');
    if (settings) {
      const parsedSettings: AppSettings = JSON.parse(settings);
      this.isTimeFormatUTC = parsedSettings.dateTimeFormatDefault === 'utc' ? true : false;
    }

    const resolveLeg = sessionStorage.getItem('leg');
    const resolveSeq = sessionStorage.getItem('sequence');

    let legs;
    let sequence;
    if (resolveLeg && resolveSeq) {
      legs = JSON.parse(resolveLeg);
      this.leg = legs;
      sequence = JSON.parse(resolveSeq);
      this.sequence = sequence;

      this.tag = designateTagType(this.leg?.isDeadhead, this.leg?.isCancelled);

      this.geoLineColor = this.getGeoLineColor();
      const coords: LatLngExpression[] = [
        [
          this.leg?.departureStation.latitude || 0,
          this.leg?.departureStation.longitude || 0,
        ],
        [
          this.leg?.arrivalStation.latitude || 0,
          this.leg?.arrivalStation.longitude || 0,
        ],
      ];
      this.crewMembers$ = this.getCrewData(legs)
      this.map = this.mapService.renderMap(0, coords, [], this.geoLineColor);
    }

  }

  ngAfterViewChecked(): void {
    this.map = this.mapService.initMap();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onResetMap(): void {
    this.mapService.initMap();
  }

  convertDate(date?: string): string | undefined {
    if (date) {
      const dateTime = DateTime.fromISO(date, { zone: 'utc' });
      return dateTime.toFormat('dd MMM yyyy');
    }
    return undefined;
  }


  onContactCrew(crewMember: CrewDetail): void {
    this._requestedEmpNum$.next(crewMember.username);
    let crewMemberDetailsExists;
    const crewMemberDetails$ = this.getCrewMemberContactInfo()

    this.ref = this.dialogService.open(CrewContactInfoComponent, {
      data: {
        data: crewMemberDetails$
      },
      header: 'Crew Member Contact',
      width: '50vw',
      modal: true,
      dismissableMask: true,
      breakpoints: {
        '500px': '98vw',
      }
    });
  }

  onHelpClicked(id: number): void {
    const header = id === 0 ? 'Flight Details' : 'Flight Times';
    this.ref = this.dialogService.open(FlightDetailDialogComponent, {
      data: id,
      header: header,
      width: '50vw',
      modal: true,
      dismissableMask: true,
      breakpoints: {
        '500px': '98vw',
      }
    });
  }

  onRegClicked(aircraft: Aircraft): void {
    this.ref = this.dialogService.open(RegDetailsDialogComponent, {
      data: aircraft,
      header: 'Aircraft Details',
      width: '50vw',
      modal: true,
      dismissableMask: true,
      breakpoints: {
        '500px': '98vw',
      }
    });
  }

  getGeoLineColor(): string {
    if (this.leg?.isCancelled) {
      return 'red';
    }
    else {
      return 'blue'
    }
  }

  getCrewData(leg: LogbookLeg): Observable<RequestState<CrewDetail[]>> {
    let crewEmpNumbers = [];
    let crews = [];
    if (!leg) {
      console.error('CrewDetails Error, no leg information')
      this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: Get crew data, no leg information`)
    }
    else {
      for (let crew of leg.flightCrew) {
        crewEmpNumbers.push(crew.employeeNumber)
        crews.push({ employeeNumber: crew.employeeNumber, seat: crew.seat })
      }

    }
    const cacheId = `crew.seq${this.sequence?.sequenceNumber}.flt${leg.flightNumber}`
    return this.signalsService.employeeNumber$.pipe(
      switchMap(() => {
        return this.flightDetailService.getCrewDetailsFor(crewEmpNumbers, cacheId)
          .pipe(
            map((crewDetail) => { 
              if (!crewDetail || !crewDetail.response || crewDetail.isLoading) {
                return new RequestState<CrewDetail[]>(
                  RequestStatus.IsCompleteNoResponse
                )
              }
              let crewMembers: CrewDetail[] = [];

              //Loop through crew details and match seat position
              for (let crew of crewDetail.response || []) {
                const crewMember = {
                  username: crew.username,
                  firstName: crew.firstName,
                  lastName: crew.lastName,
                  middleName: crew.middleName,
                  nickName: crew.nickName,
                  seat: this.matchSeatPosition(crew, crews)
                }
                crewMembers.push(crewMember);
              }

              return new RequestState<CrewDetail[]>(
                RequestStatus.IsCompleteWithResponse,
                crewMembers
              )
            })
          )
      })
    );
  }

  validateAircraftId(aircraftId?: string): string {
    if (aircraftId) {
      const trimmedAircraftId = aircraftId.trim();
      if (trimmedAircraftId === 'N') {
        return 'unknown';
      }
      else {
        return aircraftId.replace('N', '');
      }
    }
    return '';
  }

  matchSeatPosition(crewDetail: CrewDetail, flightCrews: FlightCrew[]): string {
    for (let flightcrew of flightCrews) {
      if (crewDetail.username === flightcrew.employeeNumber) {
        return flightcrew.seat
      }
    }
    return ''
  }

  getCrewMemberContactInfo(): Observable<RequestState<CrewMember>> {
    return combineLatest([
      this.requestedEmpNum$,
      this.intiatorEmpNum$
    ]).pipe(
      switchMap(([requested, initiator]) => {
        const cacheId = `reqDetail${requested}`
        return this.flightDetailService.getCrewMemberDetails(
          requested,
          initiator,
          cacheId
        ).pipe(
          map((data) => {

            if (!data || !data.response || data.hasError || data.isLoading) {

              return new RequestState<CrewMember>(
                RequestStatus.IsCompleteNoResponse,

              )

            }
            const crewMember = {
              timestamp: data.response?.timestamp || '',
              name: data.response?.name || '',
              seniority: data.response?.seniority || 0,
              employeeNumber: data.response?.employeeNumber || '',
              bidStatus: data.response?.bidStatus || '',
              apaStatus: data.response?.apaStatus || '',
              seat: this.extractSeatPosition(data.response?.bidStatus || ''),
              primaryEmail: data.response?.primaryEmail || 'unknown',
              telephone: data.response?.telephone || 'unknown',
              cell: data.response?.cell || 'unknown'
            }
            return new RequestState<CrewMember>(
              RequestStatus.IsCompleteWithResponse,
              crewMember
            )
          })
        )
      })
    );
  }

  /** Strip CA or FO from 4 part, if needed later
   * Add XA|XO for Check Airman or
   * add FE for Flight Engineer
   * But since this is current status there should
   * not be anything other than CA|FO
   */
  extractSeatPosition(bidStatus: string): string {
    const match = bidStatus.match(/(?<=\/)CA|FO(?=\/)/);
    return match ? match[0] : '';
  }
}
